/**
 * URL state helpers for the search page (spec §7.4 — filters must be
 * shareable via URL).
 *
 * Conventions:
 * - Multi-value filters (caliber, brand, bulletType, …) are encoded as
 *   repeated keys: `?caliber=9mm+Luger&caliber=.45+ACP`. Repeated keys
 *   keep the URL human-readable and let backends that already parse
 *   array params (Express does) consume them without extra work.
 * - Booleans are serialized as `key=true|false` (omitted when undefined).
 * - Numeric ranges use `minX` / `maxX`.
 * - Pagination uses `page` and `limit`.
 * - The single query string is `q`.
 *
 * The URL is the source of truth for filter state. Components should
 * read it via `parseSearchParams` and write it back via
 * `setSearchParams(toSearchParams(query))`.
 */

export type SortOption = 'relevance' | 'pricePerRound' | 'newest' | 'roundCount' | 'retailerTrust';

/**
 * Sort direction. The server accepts this as an independent dimension from
 * the sort key, letting every column be toggled asc/desc. Some sorts have a
 * natural primary direction (e.g. `pricePerRound` defaults to `asc`;
 * `newest` defaults to `desc`) but the backend honours whichever direction
 * the client sends. "relevance" is always server-ranked and ignores sortDir.
 */
export type SortDir = 'asc' | 'desc';

export type StockStatusFilter = 'in_stock' | 'out_of_stock' | 'limited' | 'unknown';

export interface SearchQuery {
  q: string;
  page: number;
  limit?: number;
  sort: SortOption;
  /** Direction for the current sort. Ignored when sort === 'relevance'. */
  sortDir: SortDir;
  caliber: string[];
  /** Broad ammo type: handgun / rifle / rimfire / shotgun (#108). */
  ammoType: string[];
  brand: string[];
  productLine: string[];
  bulletType: string[];
  bulletWeightGrains: string[];
  caseMaterial: string[];
  retailerId: string[];
  useCases: string[];
  minPricePerRound?: number;
  maxPricePerRound?: number;
  minRoundCount?: number;
  stockStatus?: StockStatusFilter;
  inStockOnly?: boolean;
  excludeRemanufactured?: boolean;
  lotNumber?: string;
}

/** Names of every multi-value (array) filter. Used to drive (de)serialization. */
export const MULTI_VALUE_KEYS = [
  'caliber',
  'ammoType',
  'brand',
  'productLine',
  'bulletType',
  'bulletWeightGrains',
  'caseMaterial',
  'retailerId',
  'useCases',
] as const satisfies ReadonlyArray<keyof SearchQuery>;

export type MultiValueKey = (typeof MULTI_VALUE_KEYS)[number];

const SORT_OPTIONS: readonly SortOption[] = [
  'relevance',
  'pricePerRound',
  'newest',
  'roundCount',
  'retailerTrust',
];

const SORT_DIR_OPTIONS: readonly SortDir[] = ['asc', 'desc'];

/**
 * Default sort direction for each sort key. These reflect the direction a
 * shopper most likely wants when they first click a column header:
 *   - pricePerRound: asc  (cheapest first)
 *   - roundCount:    desc (largest pack first)
 *   - newest:        desc (most recent first)
 *   - retailerTrust: desc (highest trust first)
 *   - relevance:     asc  (server-ranked, direction is ignored)
 */
export const DEFAULT_SORT_DIR: Record<SortOption, SortDir> = {
  relevance: 'asc',
  pricePerRound: 'asc',
  newest: 'desc',
  roundCount: 'desc',
  retailerTrust: 'desc',
};

const STOCK_OPTIONS: readonly StockStatusFilter[] = [
  'in_stock',
  'out_of_stock',
  'limited',
  'unknown',
];

function parseSort(value: string | null): SortOption {
  if (value && (SORT_OPTIONS as readonly string[]).includes(value)) {
    return value as SortOption;
  }
  return 'relevance';
}

function parseSortDir(value: string | null, sort: SortOption): SortDir {
  if (value && (SORT_DIR_OPTIONS as readonly string[]).includes(value)) {
    return value as SortDir;
  }
  return DEFAULT_SORT_DIR[sort];
}

function parseStockStatus(value: string | null): StockStatusFilter | undefined {
  if (value && (STOCK_OPTIONS as readonly string[]).includes(value)) {
    return value as StockStatusFilter;
  }
  return undefined;
}

function parseBool(value: string | null): boolean | undefined {
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return undefined;
}

function parseNumber(value: string | null): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function parseInt1(value: string | null, fallback: number): number {
  const n = parseNumber(value);
  return n != null && n >= 1 ? Math.floor(n) : fallback;
}

/**
 * Decode the current URL search params into a SearchQuery. Unknown
 * keys are ignored; missing keys fall back to sensible defaults.
 */
export function parseSearchParams(searchParams: URLSearchParams): SearchQuery {
  const multi: Record<MultiValueKey, string[]> = {
    caliber: [],
    ammoType: [],
    brand: [],
    productLine: [],
    bulletType: [],
    bulletWeightGrains: [],
    caseMaterial: [],
    retailerId: [],
    useCases: [],
  };
  for (const key of MULTI_VALUE_KEYS) {
    multi[key] = searchParams.getAll(key).filter((v) => v.length > 0);
  }

  const sort = parseSort(searchParams.get('sort'));
  const query: SearchQuery = {
    q: searchParams.get('q') ?? '',
    page: parseInt1(searchParams.get('page'), 1),
    limit: parseNumber(searchParams.get('limit')),
    sort,
    sortDir: parseSortDir(searchParams.get('sortDir'), sort),
    caliber: multi.caliber,
    ammoType: multi.ammoType,
    brand: multi.brand,
    productLine: multi.productLine,
    bulletType: multi.bulletType,
    bulletWeightGrains: multi.bulletWeightGrains,
    caseMaterial: multi.caseMaterial,
    retailerId: multi.retailerId,
    useCases: multi.useCases,
    minPricePerRound: parseNumber(searchParams.get('minPricePerRound')),
    maxPricePerRound: parseNumber(searchParams.get('maxPricePerRound')),
    minRoundCount: parseNumber(searchParams.get('minRoundCount')),
    stockStatus: parseStockStatus(searchParams.get('stockStatus')),
    inStockOnly: parseBool(searchParams.get('inStockOnly')),
    excludeRemanufactured: parseBool(searchParams.get('excludeRemanufactured')),
    lotNumber: searchParams.get('lotNumber') ?? undefined,
  };
  return query;
}

/**
 * Encode a SearchQuery into URLSearchParams. Omits empty/undefined
 * fields so the URL stays clean. Multi-value filters become repeated keys.
 */
export function toSearchParams(query: SearchQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.page && query.page !== 1) params.set('page', String(query.page));
  if (query.limit != null) params.set('limit', String(query.limit));
  if (query.sort && query.sort !== 'relevance') params.set('sort', query.sort);
  // Only write sortDir when it differs from the default for the active sort,
  // so clean/default URLs stay short (e.g. ?sort=pricePerRound implies asc).
  if (query.sort !== 'relevance' && query.sortDir !== DEFAULT_SORT_DIR[query.sort]) {
    params.set('sortDir', query.sortDir);
  }

  for (const key of MULTI_VALUE_KEYS) {
    for (const value of query[key]) {
      params.append(key, value);
    }
  }

  if (query.minPricePerRound != null) {
    params.set('minPricePerRound', String(query.minPricePerRound));
  }
  if (query.maxPricePerRound != null) {
    params.set('maxPricePerRound', String(query.maxPricePerRound));
  }
  if (query.minRoundCount != null) {
    params.set('minRoundCount', String(query.minRoundCount));
  }
  if (query.stockStatus) params.set('stockStatus', query.stockStatus);
  if (query.inStockOnly != null) params.set('inStockOnly', String(query.inStockOnly));
  if (query.excludeRemanufactured != null) {
    params.set('excludeRemanufactured', String(query.excludeRemanufactured));
  }
  if (query.lotNumber) params.set('lotNumber', query.lotNumber);

  return params;
}

/**
 * Toggle one value of a multi-value facet on/off in a URLSearchParams.
 * Returns a new URLSearchParams; does not mutate the input.
 */
export function toggleMultiValue(
  searchParams: URLSearchParams,
  key: MultiValueKey,
  value: string
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  const existing = next.getAll(key);
  next.delete(key);
  let removed = false;
  for (const v of existing) {
    if (v === value) {
      removed = true;
      continue;
    }
    next.append(key, v);
  }
  if (!removed) next.append(key, value);
  // Any filter change resets pagination.
  next.delete('page');
  return next;
}

/**
 * Remove a single filter (multi-value entry, boolean, range, or `q`)
 * from a URLSearchParams. For multi-value keys, pass `value` to remove
 * just one entry; omit it to clear them all.
 */
export function removeFilter(
  searchParams: URLSearchParams,
  key: string,
  value?: string
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  if (value === undefined) {
    next.delete(key);
  } else {
    const all = next.getAll(key);
    next.delete(key);
    for (const v of all) {
      if (v !== value) next.append(key, v);
    }
  }
  next.delete('page');
  return next;
}

/**
 * Set a single string-valued filter (e.g. lotNumber). Pass an empty
 * string to clear it.
 */
export function setSingleValue(
  searchParams: URLSearchParams,
  key: string,
  value: string | undefined
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  if (value == null || value === '') {
    next.delete(key);
  } else {
    next.set(key, value);
  }
  next.delete('page');
  return next;
}

/**
 * Update the sort param. Setting it back to "relevance" removes both `sort`
 * and `sortDir` from the URL to keep shared links short.
 *
 * When `dir` is omitted and the sort key is unchanged (i.e. the user clicked
 * the already-active column header), the direction toggles between asc/desc.
 * When the sort key changes, the direction resets to the default for that key
 * unless explicitly provided.
 */
export function setSort(
  searchParams: URLSearchParams,
  sort: SortOption,
  dir?: SortDir
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  if (sort === 'relevance') {
    next.delete('sort');
    next.delete('sortDir');
    next.delete('page');
    return next;
  }

  const prevSort = next.get('sort') ?? 'relevance';
  const prevDir = (next.get('sortDir') ?? DEFAULT_SORT_DIR[prevSort as SortOption]) as SortDir;

  let nextDir: SortDir;
  if (dir !== undefined) {
    // Caller explicitly specified a direction.
    nextDir = dir;
  } else if (prevSort === sort) {
    // Same column clicked again — toggle direction.
    nextDir = prevDir === 'asc' ? 'desc' : 'asc';
  } else {
    // New column — reset to its natural default.
    nextDir = DEFAULT_SORT_DIR[sort];
  }

  next.set('sort', sort);
  // Only write sortDir when it differs from the default to keep URLs clean.
  if (nextDir !== DEFAULT_SORT_DIR[sort]) {
    next.set('sortDir', nextDir);
  } else {
    next.delete('sortDir');
  }
  next.delete('page');
  return next;
}

/**
 * Update the page param. Page 1 is encoded as no `page=` key.
 */
export function setPage(searchParams: URLSearchParams, page: number): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  if (page <= 1) {
    next.delete('page');
  } else {
    next.set('page', String(page));
  }
  return next;
}
